import {
    ColumnDefinition,
    ColumnDefinitionsDictionary,
    getColDefsForColumnDefinitionsDictionary,
    LONG_DATE_PRECISION,
    ValueType,
} from '@instinet-analytics/semantic-ui-components';
import { enumMapComparer, SortComparator } from '@instinet-analytics/core-lib';
import { SymbolEvent } from 'widgets/corpActions/data/SymbolEvent';
import { CorporateActionType } from 'widgets/corpActions/data/CorporateActionType';
import { CorpActionTypeHolidayEnum } from 'widgets/corpActions/data/HolidayType';
import { SymbolEventsType } from 'widgets/corpActions/api/SymbolEventsRequest';
import { HolidayFlag } from 'widgets/corpActions/data/HolidayFlag';
import { IndigoAppContextType } from 'app/AppContext';
import React from 'react';
import isNil from 'lodash/isNil';
import { GridDetailColDef } from 'utils/components/GridDetailsPanel/GridDetailsPanel';

export type SymbolEventColumn = keyof Omit<
    Omit<SymbolEvent, 'uuid'>,
    'expectedRptTimestamp'
>;

const dateCol = { valueType: ValueType.DATE, precision: LONG_DATE_PRECISION };

export const corpActionGroups: Record<
    CorporateActionType,
    Array<SymbolEventColumn>
> = {
    [CorporateActionType.CHG_ID]: [
        'oldCusip',
        'newCusip',
        'oldIsin',
        'newIsin',
        'oldSedol',
        'newSedol',
    ],
    [CorporateActionType.CHG_LIST]: ['oldExch', 'newExch'],
    [CorporateActionType.CHG_NAME]: ['oldName', 'newName'],
    [CorporateActionType.CHG_RLOT]: ['oldRlot', 'newRlot'],
    [CorporateActionType.CHG_TKR]: ['oldTicker', 'newTicker'],
    [CorporateActionType.DELIST]: ['delistExch', 'delistReason'],
    [CorporateActionType.DVD_CASH]: [
        'adjustmentFactor',
        'dividendCurrency',
        'dividendFreq',
        'dividendGrossAmount',
        'recordDate',
        'payDate',
        'capitalChangeType',
    ],
    [CorporateActionType.DVD_STOCK]: [
        'adjustmentFactor',
        'recordDate',
        'payDate',
        'capitalChangeType',
        'bonusAmount',
    ],
    [CorporateActionType.EQY_OFFER]: [
        'offerType',
        'shareOffer',
        'amountOffer',
        'offerPrice',
        'offerCurrency',
    ],
    [CorporateActionType.LIST]: ['listingExch', 'listingSecType'],
    [CorporateActionType.RIGHTS_OFFER]: [
        'adjustmentFactor',
        'recordDate',
        'stockTicker',
        'payDate',
        'ccRatio',
        'ccTerms',
        'ccPrice',
        'ccTicker2',
        'ccCurrency',
        'tradeStartDate',
        'tradeEndDate',
    ],
    [CorporateActionType.SPIN]: [
        'adjustmentFactor',
        'recordDate',
        'payDate',
        'stockTicker',
    ],
    [CorporateActionType.STOCK_SPLT]: [
        'adjustmentFactor',
        'recordDate',
        'payDate',
        'capitalChangeType',
        'ccRatio',
        'ccTerms',
    ],
    [CorporateActionType.EARN_RLS]: ['expectedRptDate', 'expectedRptType'],
    [CorporateActionType.ACQUIS]: [
        'adType',
        'adCash',
        'adShares',
        'adStat',
        'adCurrency',
        'adAcquirerTicker',
        'adTargetTicker',
        'adFlag',
        'adDateFlag',
        'adPctOwned',
        'adPctSought',
        'adUnsolicited',
    ],
    [CorporateActionType.BANCR]: ['bankruptcyType'],
    [CorporateActionType.CRNCY_QT_CHNG]: ['oldCurrency', 'newCurrency'],
    [CorporateActionType.DIVEST]: [
        'adType',
        'adCash',
        'adShares',
        'adStat',
        'adCurrency',
        'adAcquirerTicker',
        'adTargetTicker',
        'adUnit',
        'adAcquiredBy',
        'adAmt',
        'adCashFlag',
        'adSharesFlag',
    ],
    [CorporateActionType.PARTIAL_PAY]: [
        'ppId',
        'ppInstallAmt',
        'ppInstallDate',
        'ppInstallType',
    ],
    [CorporateActionType.RECLASS]: [
        'oldClass',
        'newClass',
        'reClassType',
        'reclassTerms',
        'reclassRatio',
    ],
    [CorporateActionType.SH_HOLDER_MEET]: ['newsRecordDate', 'meetingType'],
    [CorporateActionType.STOCK_BUY]: [
        'sbAmt',
        'sbType',
        'sbCompletedDate',
        'sbPrice',
        'sbShares',
        'sbValuePurchased',
        'sbCurrency',
    ],
    [CorporateActionType.CHG_DOM]: ['oldCountry', 'newCountry'],
};

export const holidayGroup: Array<SymbolEventColumn> = [
    'holidayDescription',
    'holidayType',
    'holidayFlag',
    'holidayCountry',
];

const colGroups = {
    ...corpActionGroups,
    [CorpActionTypeHolidayEnum.H]: holidayGroup,
};

const lookup = Object.keys(colGroups).reduce<Record<string, string[]>>(
    (ret, gn) => {
        const symbolEventType = gn as keyof typeof colGroups;
        const group = colGroups[symbolEventType];
        for (const col of group) {
            if (!ret[col]) {
                ret[col] = [];
            }
            ret[col]!.push(symbolEventType);
        }
        return ret;
    },
    {}
);

export const descriptions: Record<SymbolEventColumn, string> = {
    adjustmentFactor: 'Adjustment Factor',
    amdDate: 'Amended Date',
    amountOffer: 'Amount Offer',
    announcementDate: 'Announcement Date',
    bloombergSymbol: 'Bloomberg Symbol',
    caId: 'Id',
    type: 'Event Type',
    delistExch: 'Delisting Exchange',
    delistReason: 'Delisting Reason',
    dividendCurrency: 'Dividend Currency',
    dividendGrossAmount: 'Dividend Gross Amount',
    effectiveDate: 'Effective Date',
    exDate: 'Ex Date',
    expectedRptDate: 'Expected Earnings Date',
    expectedRptType: 'Expected Earnings Status',
    listingExch: 'Listing Exchange',
    listingSecType: 'Security Type',
    newCusip: 'New CUSIP',
    newExch: 'New Exchange',
    newIsin: 'New ISIN',
    newName: 'New Name',
    newRlot: 'New Round Lot',
    newSedol: 'New SEDOL',
    newTicker: 'New Ticker',
    notes: 'Notes',
    offerCurrency: 'Offer Currency',
    offerPrice: 'Offer Price',
    offerType: 'Offer Type',
    oldCusip: 'Old CUSIP',
    oldExch: 'Old Exchange',
    oldIsin: 'Old ISIN',
    oldName: 'Old Name',
    oldRlot: 'Old Round Lot',
    oldSedol: 'Old SEDOL',
    oldTicker: 'Old Ticker',
    payDate: 'Pay Date',
    recordDate: 'Record Date',
    shareOffer: 'Share Offer',
    stockTicker: 'Stock Ticker',
    symbol: 'Symbol',
    adType: 'Acquisition Type',
    adCash: 'Cash',
    adShares: 'Shares',
    adStat: 'Status',
    adCurrency: 'Currency',
    adAcquirerTicker: 'Acquirer Ticker',
    adTargetTicker: 'Target Ticker',
    adFlag: 'Flag',
    adDateFlag: 'Date Flag',
    adPctOwned: 'Percent Owned',
    adPctSought: 'Percent Sought',
    adUnsolicited: 'Unsolicited',
    adUnit: 'Unit',
    adAcquiredBy: 'Acquired By',
    adAmt: 'Amount',
    adCashFlag: 'Cash Flag',
    adSharesFlag: 'Shares Flag',
    bankruptcyType: 'Bankruptcy Type',
    newsRecordDate: 'Record Date',
    meetingType: 'Meeting Type',
    ppId: 'Id',
    ppInstallAmt: 'Install Amount',
    ppInstallDate: 'Install Date',
    ppInstallType: 'Install Type',
    oldClass: 'Old Class',
    newClass: 'New Class',
    reClassType: 'Reclass type',
    reclassTerms: 'Reclass Terms',
    reclassRatio: 'Reclass Ratio',
    sbAmt: 'Amount',
    sbType: 'Stock Buy Type',
    sbCompletedDate: 'Completed Date',
    sbPrice: 'Price',
    sbShares: 'Shares',
    sbValuePurchased: 'Value Purchased',
    sbCurrency: 'Currency',
    oldCurrency: 'Old Currency',
    newCurrency: 'New Currency',
    oldCountry: 'Old Country',
    newCountry: 'New Country',
    capitalChangeType: 'Capital Change Type',
    dividendFreq: 'Dividend Frequency',
    bonusAmount: 'Bonus Amount',
    ccRatio: 'Ratio',
    ccTerms: 'Terms',
    ccPrice: 'Price',
    ccTicker2: 'Ticker 2',
    ccCurrency: 'Currency',
    tradeStartDate: 'Trade Start Date',
    tradeEndDate: 'Trade End Date',
    expectedRptTime: 'Expected Report Time',
    holidayDescription: 'Description',
    holidayCountry: 'Country',
    holidayType: 'Holiday Type',
    holidayFlag: 'Holiday Flag',
};

const getTag = (name: string, groups?: string[]) =>
    groups ? `${groups.join('|')}/${name}` : `All/${name}`;

const getCol = (colName: SymbolEventColumn): ColumnDefinition => {
    const groups = lookup[colName];
    const name = descriptions[colName];
    const tag = getTag(colName, groups);
    return {
        field: colName,
        name,
        valueType: ValueType.STRING,
        tag,
        textAlign: 'left',
    };
};

export const corporateActionsColumnDefinitionsDictionary: ColumnDefinitionsDictionary<SymbolEventColumn> =
    {
        adjustmentFactor: {
            ...getCol('adjustmentFactor'),
            valueType: ValueType.NUMBER,
            precision: 4,
            width: 140,
            textAlign: 'right',
        },
        amdDate: {
            ...getCol('amdDate'),
            ...dateCol,
            width: 120,
        },
        amountOffer: {
            ...getCol('amountOffer'),
            valueType: ValueType.NUMBER,
            width: 110,
            precision: 4,
            textAlign: 'right',
        },
        announcementDate: {
            ...getCol('announcementDate'),
            ...dateCol,
            width: 150,
        },
        bloombergSymbol: {
            ...getCol('bloombergSymbol'),
            width: 140,
        },
        caId: {
            ...getCol('caId'),
            valueType: ValueType.NUMBER,
            width: 80,
            raw: true,
        },
        type: {
            ...getCol('type'),
            valueType: ValueType.ENUM,
            enumMap: SymbolEventsType,
            sortCompareFunction: enumMapComparer(
                SymbolEventsType as {
                    [key: string]: string;
                }
            ) as SortComparator<unknown>,
            width: 100,
        },
        delistExch: {
            ...getCol('delistExch'),
            width: 140,
        },
        delistReason: {
            ...getCol('delistReason'),
            width: 140,
        },
        dividendCurrency: {
            ...getCol('dividendCurrency'),
            width: 140,
        },
        dividendGrossAmount: {
            ...getCol('dividendGrossAmount'),
            valueType: ValueType.NUMBER,
            precision: 4,
            width: 160,
            textAlign: 'right',
        },
        effectiveDate: {
            ...getCol('effectiveDate'),
            ...dateCol,
            width: 120,
        },
        exDate: {
            ...getCol('exDate'),
            ...dateCol,
            width: 80,
        },
        expectedRptDate: {
            ...getCol('expectedRptDate'),
            ...dateCol,
            width: 160,
        },
        expectedRptType: {
            ...getCol('expectedRptType'),
            width: 170,
        },
        listingExch: {
            ...getCol('listingExch'),
        },
        listingSecType: {
            ...getCol('listingSecType'),
            width: 120,
        },
        newCusip: {
            ...getCol('newCusip'),
            width: 120,
        },
        newExch: {
            ...getCol('newExch'),
            width: 120,
        },
        newIsin: {
            ...getCol('newIsin'),
            width: 120,
        },
        newName: {
            ...getCol('newName'),
            width: 120,
        },
        newRlot: {
            ...getCol('newRlot'),
            valueType: ValueType.NUMBER,
            textAlign: 'right',
            precision: 4,
        },
        newSedol: {
            ...getCol('newSedol'),
            width: 120,
        },
        newTicker: {
            ...getCol('newTicker'),
            width: 120,
        },
        notes: {
            ...getCol('notes'),
        },
        offerCurrency: {
            ...getCol('offerCurrency'),
        },
        offerPrice: {
            ...getCol('offerPrice'),
            valueType: ValueType.NUMBER,
            width: 100,
            textAlign: 'right',
            precision: 4,
        },
        offerType: {
            ...getCol('offerType'),
        },
        oldCusip: {
            ...getCol('oldCusip'),
            width: 120,
        },
        oldExch: {
            ...getCol('oldExch'),
            width: 120,
        },
        oldIsin: {
            ...getCol('oldIsin'),
            width: 120,
        },
        oldName: {
            ...getCol('oldName'),
            width: 120,
        },
        oldRlot: {
            ...getCol('oldRlot'),
            valueType: ValueType.NUMBER,
            width: 120,
            textAlign: 'right',
            precision: 0,
        },
        oldSedol: {
            ...getCol('oldSedol'),
            width: 120,
        },
        oldTicker: {
            ...getCol('oldTicker'),
            width: 120,
        },
        payDate: {
            ...getCol('payDate'),
            ...dateCol,
            width: 100,
        },
        recordDate: {
            ...getCol('recordDate'),
            ...dateCol,
            width: 100,
        },
        shareOffer: {
            ...getCol('shareOffer'),
            valueType: ValueType.NUMBER,
            precision: 4,
            textAlign: 'right',
            width: 100,
        },
        stockTicker: {
            ...getCol('stockTicker'),
            width: 100,
        },
        symbol: {
            ...getCol('symbol'),
            width: 80,
        },
        capitalChangeType: {
            ...getCol('capitalChangeType'),
            width: 80,
        },
        dividendFreq: {
            ...getCol('dividendFreq'),
            width: 80,
        },
        bonusAmount: {
            ...getCol('bonusAmount'),
            textAlign: 'right',
            width: 80,
        },
        ccRatio: {
            ...getCol('ccRatio'),
            textAlign: 'right',
            width: 80,
        },
        ccTerms: {
            ...getCol('ccTerms'),
            width: 80,
        },
        ccPrice: {
            ...getCol('ccPrice'),
            textAlign: 'right',
            width: 80,
        },
        ccTicker2: {
            ...getCol('ccTicker2'),
            width: 80,
        },
        ccCurrency: {
            ...getCol('ccCurrency'),
            width: 80,
        },
        tradeStartDate: {
            ...getCol('tradeStartDate'),
            width: 80,
        },
        tradeEndDate: {
            ...getCol('tradeEndDate'),
            width: 80,
        },
        expectedRptTime: {
            ...getCol('expectedRptTime'),
            width: 80,
        },
        adType: {
            ...getCol('adType'),
            width: 80,
        },
        adCash: {
            ...getCol('adCash'),
            textAlign: 'right',
            width: 80,
        },
        adShares: {
            ...getCol('adShares'),
            textAlign: 'right',
            width: 80,
        },
        adStat: {
            ...getCol('adStat'),
            width: 80,
        },
        adCurrency: {
            ...getCol('adCurrency'),
            width: 80,
        },
        adAcquirerTicker: {
            ...getCol('adAcquirerTicker'),
            width: 80,
        },
        adTargetTicker: {
            ...getCol('adTargetTicker'),
            width: 80,
        },
        adFlag: {
            ...getCol('adFlag'),
            width: 80,
        },
        adDateFlag: {
            ...getCol('adDateFlag'),
            width: 80,
        },
        adPctOwned: {
            ...getCol('adPctOwned'),
            textAlign: 'right',
            width: 80,
        },
        adPctSought: {
            ...getCol('adPctSought'),
            textAlign: 'right',
            width: 80,
        },
        adUnsolicited: {
            ...getCol('adUnsolicited'),
            width: 80,
        },
        adUnit: {
            ...getCol('adUnit'),
            width: 80,
        },
        adAcquiredBy: {
            ...getCol('adAcquiredBy'),
            width: 80,
        },
        adAmt: {
            ...getCol('adAmt'),
            width: 80,
        },
        adCashFlag: {
            ...getCol('adCashFlag'),
            width: 80,
        },
        adSharesFlag: {
            ...getCol('adSharesFlag'),
            width: 80,
        },
        bankruptcyType: {
            ...getCol('bankruptcyType'),
            width: 80,
        },
        newsRecordDate: {
            ...getCol('newsRecordDate'),
            width: 80,
        },
        meetingType: {
            ...getCol('meetingType'),
            width: 80,
        },
        ppId: {
            ...getCol('ppId'),
            width: 80,
        },
        ppInstallAmt: {
            ...getCol('ppInstallAmt'),
            textAlign: 'right',
            width: 80,
        },
        ppInstallDate: {
            ...getCol('ppInstallDate'),
            width: 80,
        },
        ppInstallType: {
            ...getCol('ppInstallType'),
            width: 80,
        },
        oldClass: {
            ...getCol('oldClass'),
            width: 80,
        },
        newClass: {
            ...getCol('newClass'),
            width: 80,
        },
        reClassType: {
            ...getCol('reClassType'),
            width: 80,
        },
        reclassTerms: {
            ...getCol('reclassTerms'),
            width: 80,
        },
        reclassRatio: {
            ...getCol('reclassRatio'),
            textAlign: 'right',
            width: 80,
        },
        sbAmt: {
            ...getCol('sbAmt'),
            textAlign: 'right',
            width: 80,
        },
        sbType: {
            ...getCol('sbType'),
            width: 80,
        },
        sbCompletedDate: {
            ...getCol('sbCompletedDate'),
            width: 80,
        },
        sbPrice: {
            ...getCol('sbPrice'),
            textAlign: 'right',
            width: 80,
        },
        sbShares: {
            ...getCol('sbShares'),
            textAlign: 'right',
            width: 80,
        },
        sbValuePurchased: {
            ...getCol('sbValuePurchased'),
            width: 80,
        },
        sbCurrency: {
            ...getCol('sbCurrency'),
            width: 80,
        },
        oldCurrency: {
            ...getCol('oldCurrency'),
            width: 80,
        },
        newCurrency: {
            ...getCol('newCurrency'),
            width: 80,
        },
        oldCountry: {
            ...getCol('oldCountry'),
            width: 80,
        },
        newCountry: {
            ...getCol('newCountry'),
            width: 80,
        },
        holidayDescription: {
            ...getCol('holidayDescription'),
            width: 140,
        },
        holidayCountry: {
            ...getCol('holidayCountry'),
            width: 120,
        },
        holidayType: {
            ...getCol('holidayType'),
            width: 120,
        },
        holidayFlag: {
            ...getCol('holidayFlag'),
            width: 80,
            valueType: ValueType.ENUM,
            enumMap: HolidayFlag,
            sortCompareFunction: enumMapComparer(
                HolidayFlag
            ) as SortComparator<unknown>,
        },
    };

export const corporateActionsColumnDefinitions: ColumnDefinition[] =
    Object.values(corporateActionsColumnDefinitionsDictionary);

const getExpectedRptTimeDisplayValue = (
    context: IndigoAppContextType,
    data?: SymbolEvent
) => {
    if (!data) {
        return '';
    }
    const { expectedRptTime, expectedRptTimestamp } = data;

    if (expectedRptTimestamp) {
        return context.timeFormat.toTimeMinutes(expectedRptTimestamp);
    }

    return expectedRptTime ?? '';
};

export const expectedRptTimeCellRenderer =
    (context: IndigoAppContextType) =>
    // eslint-disable-next-line react/display-name
    (props: { data: SymbolEvent }) => {
        return (
            <span>{getExpectedRptTimeDisplayValue(context, props.data)}</span>
        );
    };

export function getCorporateActionsColDefs(context: IndigoAppContextType) {
    const colDefs = getColDefsForColumnDefinitionsDictionary(
        context,
        corporateActionsColumnDefinitionsDictionary
    );
    const expectedRptTime = colDefs.find(
        (c) => c.colId === 'expectedRptTime'
    ) as GridDetailColDef<SymbolEvent>;
    expectedRptTime.cellRenderer = expectedRptTimeCellRenderer(context);
    expectedRptTime.getExportValue = (_value, data) =>
        getExpectedRptTimeDisplayValue(context, data);
    expectedRptTime.showDetailValue = (data: SymbolEvent | undefined) =>
        !isNil(data?.expectedRptTime) || !isNil(data?.expectedRptTimestamp);
    return colDefs;
}
